<template>
  <div class="purchase-order-view">
    <el-alert v-if="loading" :title="$t('loading')" type="info" show-icon class="mb-4" />
    <el-alert v-else-if="error" :title="error" type="error" show-icon class="mb-4" />
    <div v-else>
      <el-page-header @back="$router.back()" :content="$t('supply.label.details')" class="mb-3" />
      <el-card shadow="never" class="mb-4">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <div>
            <h2 class="mb-0">{{ po.code }}</h2>
            <span class="po-status" :class="'text-' + po.status_colour">{{ $t('supply.label.statuses.' + po.status) }}</span>
          </div>
          <div>
            <el-button-group>
              <el-button type="success" icon="el-icon-bottom">{{ $t('supply.button.receive') }}</el-button>
              <el-button type="primary" icon="el-icon-edit">{{ $t('button.edit') }}</el-button>
              <el-button type="info" icon="el-icon-message" @click="openEmailDialog">{{ $t('supply.button.send') }}</el-button>
              <el-button type="default" icon="el-icon-printer" @click="printOrder">{{ $t('button.print') }}</el-button>
              <el-dropdown>
                <el-button type="default" icon="el-icon-more"></el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item @click.native="printOrder">{{ $t('supply.label.print') }}</el-dropdown-item>
                  <el-dropdown-item>{{ $t('supply.label.delete') }}</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </el-button-group>
          </div>
        </div>
        <el-row :gutter="24" class="mb-2">
          <el-col :span="12">
            <div class="mb-1"><strong>{{ $t('supply.label.date') }}:</strong> {{ formatDate(po.date) }}</div>
            <div class="mb-1"><strong>{{ $t('supply.label.ordered_by') }}:</strong> {{ po.user && po.user.name }}</div>
            <div class="mb-1"><strong>{{ $t('supply.label.expected_date') }}:</strong> {{ formatDate(po.expected_date) }}</div>
          </el-col>
          <el-col :span="12">
            <el-progress :percentage="receivedPercentage" :text-inside="true" status="success" class="float-right" style="width: 220px" :stroke-width="16" />
            &nbsp;
            <div class="text-right text-muted" style="font-size: 13px;">{{ $t('supply.label.received') }} {{ receivedCount }} / {{ totalCount }}</div>
          </el-col>
        </el-row>
        <el-row :gutter="24">
          <el-col :span="12">
            <div class="mb-1"><strong>{{ $t('supply.label.supplier') }}:</strong></div>
            <div>{{ po.supplier && po.supplier.name }}</div>
            <div>{{ po.supplier && po.supplier.phone }}</div>
            <div>{{ po.supplier && po.supplier.email }}</div>
          </el-col>
          <el-col :span="12">
            <div class="mb-1"><strong>{{ $t('supply.label.destination_store') }}:</strong></div>
            <div>{{ po.store && po.store.name }}</div>
            <div>{{ po.store && po.store.address }}</div>
          </el-col>
        </el-row>
        <div v-if="po.note" class="mt-2"><el-tag type="info">{{ $t('supply.label.note') }}: {{ po.note }}</el-tag></div>
      </el-card>
      <el-card shadow="never" class="mb-4">
        <h4 class="mb-2">{{ $t('supply.label.items') }}</h4>
        <el-table :data="po.items" border stripe>
          <el-table-column prop="item.name" :label="$t('supply.label.item')" />
          <el-table-column prop="item.code" :label="$t('supply.label.sku')" />
          <el-table-column prop="quantity" :label="$t('supply.label.quantity')" />
          <el-table-column :label="$t('supply.label.received_qty')">
            <template slot-scope="scope">
              <el-progress :percentage="itemReceivedPercent(scope.row)" :text-inside="true" :stroke-width="16" status="success" style="width: 120px;" />
              <span class="ml-2">{{ receivedQty(scope.row) }} / {{ scope.row.quantity }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="unit_price" :label="$t('supply.label.purchase_cost')">
            <template slot-scope="scope">{{ formatPrice(scope.row.unit_price) }}</template>
          </el-table-column>
          <el-table-column prop="total_cost" :label="$t('supply.label.amount')">
            <template slot-scope="scope">{{ formatPrice(scope.row.total_cost) }}</template>
          </el-table-column>
        </el-table>
        <div v-if="!po.items.length" class="text-center text-muted mt-3">{{ $t('supply.message.no_items') }}</div>
      </el-card>
      <el-row :gutter="24" class="mb-4">
        <el-col :span="12">
          <el-card shadow="never">
            <h4 class="mb-2">{{ $t('supply.label.additional_cost') }}</h4>
            <el-table :data="po.additional_cost" border stripe>
              <el-table-column prop="name" :label="$t('supply.label.additional_cost')" />
              <el-table-column prop="amount" :label="$t('supply.label.amount')">
                <template slot-scope="scope">{{ formatPrice(scope.row.amount) }}</template>
              </el-table-column>
              <el-table-column prop="is_applied" :label="$t('supply.label.status')">
                <template slot-scope="scope">
                  <el-tag v-if="scope.row.is_applied" type="success">Applied</el-tag>
                  <el-tag v-else-if="scope.row.is_pending" type="warning">Pending</el-tag>
                  <el-tag v-else type="info">Other</el-tag>
                </template>
              </el-table-column>
            </el-table>
            <div v-if="!po.additional_cost.length" class="text-center text-muted mt-3">{{ $t('supply.message.no_additional_cost') }}</div>
          </el-card>
        </el-col>
        <el-col :span="12">
          <el-card shadow="never">
            <h4 class="mb-2">{{ $t('supply.label.total') }}</h4>
            <div class="d-flex flex-column align-items-end">
              <div><span class="font-weight-bold">{{ $t('supply.label.total') }}:</span> <span class="font-weight-bold ml-2">{{ formatPrice(po.total_cost) }}</span></div>
              <div v-for="cost in po.additional_cost" :key="cost.id">
                <span>{{ cost.name }}:</span> <span :class="{'text-danger': cost.amount < 0, 'text-success': cost.amount > 0}">{{ formatPrice(cost.amount) }}</span>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
      <email-dialog v-if="showEmailDialog" :visible.sync="showEmailDialog" :supply="po"  :display="showEmailDialog"/>
    </div>
  </div>
</template>

<script>
import { shortDateFormat, formatPrice } from '@/packages/helpers'
import EmailDialog from '../Email/EmailDialog.vue'

export default {
  name: 'PurchaseOrder',
  components: { EmailDialog },
  data () {
    return {
      loading: true,
      error: '',
      po: {},
      showEmailDialog: false
    }
  },
  computed: {
    receivedCount () {
      return this.po.items ? this.po.items.reduce((sum, item) => sum + this.receivedQty(item), 0) : 0
    },
    totalCount () {
      return this.po.items ? this.po.items.reduce((sum, item) => sum + (item.quantity || 0), 0) : 0
    },
    receivedPercentage () {
      if (!this.totalCount) return 0
      const percent = Math.round((this.receivedCount / this.totalCount) * 100)
      return isNaN(percent) ? 0 : Math.min(Math.max(percent, 0), 100)
    }
  },
  methods: {
    formatDate (date) {
      return shortDateFormat(date)
    },
    formatPrice (val) {
      return formatPrice(val)
    },
    receivedQty (item) {
      // Sum received quantity for this item across all received batches
      if (!this.po.received) return 0
      let qty = 0
      this.po.received.forEach(batch => {
        if (batch.items && batch.items.length) {
          batch.items.forEach(rItem => {
            if (rItem.item_id === item.item_id) {
              qty += rItem.quantity
            }
          })
        }
      })
      return qty
    },
    itemReceivedPercent (item) {
      const ordered = item.quantity || 0
      const received = this.receivedQty(item)
      if (!ordered) return 0
      const percent = Math.round((received / ordered) * 100)
      return isNaN(percent) ? 0 : Math.min(Math.max(percent, 0), 100)
    },
    printOrder () {
      // Print only the PO details section
      const printContents = this.$el.querySelector('.el-card').outerHTML
      const win = window.open('', '', 'height=700,width=900')
      win.document.write('<html><head><title>Print</title>')
      win.document.write('<link rel="stylesheet" href="/assets/css/app.css">')
      win.document.write('</head><body >')
      win.document.write(printContents)
      win.document.write('</body></html>')
      win.document.close()
      win.focus()
      setTimeout(() => win.print(), 500)
    },
    openEmailDialog () {
      this.showEmailDialog = true
    }
  },
  async created () {
    try {
      this.loading = true
      const id = this.$route.params.id
      this.po = await this.$supplyService.fetch(id)
    } catch (e) {
      this.error = e.message || 'Failed to load purchase order.'
    } finally {
      this.loading = false
    }
  }
}
</script>

<style scoped>
.purchase-order-view {
  max-width: 900px;
  margin: 0 auto;
  padding: 24px 0;
}
.po-status {
  font-size: 1rem;
  font-weight: 600;
  margin-left: 1rem;
}
.text-warning { color: #e6a23c; }
.text-success { color: #67c23a; }
.text-danger { color: #f56c6c; }
.text-info { color: #909399; }
.el-progress-bar__innerText { color: #fff !important; }
</style>
